import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Lightning,
  Bell,
  Translate,
  Star,
  CreditCard,
  CalendarBlank,
  Infinity,
  ArrowRight,
  Lock,
  Package,
  Envelope,
} from "phosphor-react";
import { useEntitlements, useLimitUI } from "../contexts/EntitlementContext";

const FEATURE_CONFIG = {
  spanish_translation: {
    slug: "spanish_translation",
    name: "Spanish Translation",
    icon: Translate,
    ctaText: "Enable Spanish Translation",
    description: "Translate content for Spanish-speaking technicians and customers",
  },
  unlimited_usage: {
    slug: "unlimited_usage",
    name: "Unlimited Usage",
    icon: Infinity,
    ctaText: "Unlock Unlimited Usage",
    description: "Remove all job and photo limits",
  },
  online_booking: {
    slug: "online_booking",
    name: "Online Booking",
    icon: CalendarBlank,
    ctaText: "Enable Online Booking",
    description: "Let customers book appointments on your website",
  },
  appointment_reminders: {
    slug: "appointment_reminders",
    name: "Appointment Reminders",
    icon: Bell,
    ctaText: "Enable Appointment Reminders",
    description: "Send automated reminders via email and SMS",
  },
  review_collection: {
    slug: "review_collection",
    name: "Review Collection",
    icon: Star,
    ctaText: "Enable Review Collection",
    description: "Automatically request Google reviews after jobs",
  },
  payment_processing: {
    slug: "payment_processing",
    name: "Payment Processing",
    icon: CreditCard,
    ctaText: "Enable Payments",
    description: "Accept card payments from customers",
  },
  email_marketing: {
    slug: "email_marketing",
    name: "Email Marketing",
    icon: Envelope,
    ctaText: "Enable Email Marketing",
    description: "Send campaigns and manage customer lists",
  },
};

/**
 * Simple inline CTA button - only shows when feature is globally active and not owned
 */
export function FeatureCTAButton({ featureSlug, size = "default", className = "" }) {
  const navigate = useNavigate();
  const { shouldShowUpgradeCTA, loading } = useEntitlements();
  const config = FEATURE_CONFIG[featureSlug];

  // Don't render if feature config doesn't exist, loading, or shouldn't show CTA
  if (!config || loading || !shouldShowUpgradeCTA(featureSlug)) {
    return null;
  }

  const Icon = config.icon;

  return (
    <Button
      size={size}
      variant="outline"
      onClick={() => navigate(`/settings/upgrades?feature=${featureSlug}`)}
      className={`border-[#FF6600] text-[#FF6600] hover:bg-[#FF6600] hover:text-white ${className}`}
    >
      <Icon size={size === "sm" ? 14 : 16} className="mr-1" />
      {config.ctaText}
    </Button>
  );
}

/**
 * Card CTA for more prominent placement
 * Only shows when feature is globally active and not owned
 */
export function FeatureCTACard({ featureSlug, compact = false }) {
  const navigate = useNavigate();
  const { shouldShowUpgradeCTA, loading } = useEntitlements();
  const config = FEATURE_CONFIG[featureSlug];

  if (!config || loading || !shouldShowUpgradeCTA(featureSlug)) {
    return null;
  }

  const Icon = config.icon;

  if (compact) {
    return (
      <div
        className="flex items-center gap-3 p-3 bg-[#FF6600]/5 border border-[#FF6600]/20 rounded-sm cursor-pointer hover:bg-[#FF6600]/10 transition-colors"
        onClick={() => navigate(`/settings/upgrades?feature=${featureSlug}`)}
      >
        <div className="w-8 h-8 rounded-full bg-[#FF6600]/20 flex items-center justify-center">
          <Icon size={16} className="text-[#FF6600]" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-[#FF6600]">{config.ctaText}</p>
        </div>
        <ArrowRight size={16} className="text-[#FF6600]" />
      </div>
    );
  }

  return (
    <Card className="border-2 border-[#FF6600]/30 bg-gradient-to-r from-[#FF6600]/5 to-transparent">
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-[#FF6600]/20 flex items-center justify-center">
            <Icon size={24} className="text-[#FF6600]" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium">{config.name}</h4>
            <p className="text-sm text-zinc-500">{config.description}</p>
          </div>
          <Button
            onClick={() => navigate(`/settings/upgrades?feature=${featureSlug}`)}
            className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
          >
            {config.ctaText}
            <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Locked feature overlay - shows lock when feature is active but not owned
 * Renders nothing if feature is globally inactive
 */
export function LockedFeatureOverlay({ featureSlug, children }) {
  const navigate = useNavigate();
  const { isEntitled, isFeatureActive, loading } = useEntitlements();
  const config = FEATURE_CONFIG[featureSlug];

  // If feature is globally inactive, just render children normally (no lock)
  if (!config || loading || !isFeatureActive(featureSlug)) {
    return children;
  }

  // If user is entitled, render children normally
  if (isEntitled(featureSlug)) {
    return children;
  }

  const Icon = config.icon;

  // Show locked overlay
  return (
    <div className="relative">
      <div className="opacity-30 pointer-events-none">{children}</div>
      <div className="absolute inset-0 flex items-center justify-center bg-zinc-50/80 backdrop-blur-sm">
        <div className="text-center p-6">
          <div className="w-16 h-16 rounded-full bg-zinc-200 mx-auto mb-4 flex items-center justify-center">
            <Lock size={24} className="text-zinc-500" />
          </div>
          <h4 className="font-medium text-zinc-900 mb-2">{config.name}</h4>
          <p className="text-sm text-zinc-500 mb-4">{config.description}</p>
          <Button
            onClick={() => navigate(`/settings/upgrades?feature=${featureSlug}`)}
            className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
          >
            <Icon size={16} className="mr-2" />
            {config.ctaText}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Usage limit warning - only shows when:
 * 1. Unlimited Usage feature is globally active
 * 2. User is approaching or at their limit
 * 3. User doesn't have unlimited
 * 
 * HIDES COMPLETELY when Unlimited Usage feature is globally inactive
 */
export function UsageLimitWarning({ currentUsage, limitType, showCTA = true }) {
  const navigate = useNavigate();
  const { showLimitUI, hasUnlimited, jobLimit, photoLimit } = useLimitUI();

  // Don't show anything if Unlimited Usage feature is globally inactive
  if (!showLimitUI) {
    return null;
  }

  // If user has unlimited, don't show warning
  if (hasUnlimited) {
    return null;
  }

  const limit = limitType === "job_limit" ? jobLimit : photoLimit;

  // If no limit defined, don't show
  if (limit === null || limit === Infinity || limit === 0) {
    return null;
  }

  const percentage = (currentUsage / limit) * 100;

  // Only show when at 80% or above
  if (percentage < 80) return null;

  const isAtLimit = currentUsage >= limit;

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-sm ${
        isAtLimit
          ? "bg-red-50 border border-red-200"
          : "bg-amber-50 border border-amber-200"
      }`}
    >
      <Infinity
        size={20}
        className={isAtLimit ? "text-red-500" : "text-amber-500"}
      />
      <div className="flex-1">
        <p
          className={`text-sm font-medium ${
            isAtLimit ? "text-red-700" : "text-amber-700"
          }`}
        >
          {isAtLimit
            ? `You've reached your ${limitType.replace("_", " ").replace("limit", "")} limit`
            : `${currentUsage} of ${limit} ${limitType.replace("_limit", "s")} used`}
        </p>
      </div>
      {showCTA && (
        <Button
          size="sm"
          onClick={() => navigate("/settings/upgrades?feature=unlimited_usage")}
          className={
            isAtLimit
              ? "bg-red-600 hover:bg-red-700 text-white"
              : "bg-amber-600 hover:bg-amber-700 text-white"
          }
        >
          Upgrade
        </Button>
      )}
    </div>
  );
}

/**
 * Usage limit display - shows current usage vs limit
 * HIDES COMPLETELY when Unlimited Usage feature is globally inactive
 */
export function UsageLimitDisplay({ currentUsage, limitType, label }) {
  const { showLimitUI, hasUnlimited, jobLimit, photoLimit } = useLimitUI();

  // Don't show anything if Unlimited Usage feature is globally inactive
  if (!showLimitUI) {
    return null;
  }

  const limit = limitType === "job_limit" ? jobLimit : photoLimit;

  // If user has unlimited
  if (hasUnlimited || limit === Infinity) {
    return (
      <div className="text-sm text-zinc-500">
        <span className="font-medium text-zinc-900">{currentUsage}</span> {label}
        <Badge variant="outline" className="ml-2 text-emerald-600 border-emerald-300">
          Unlimited
        </Badge>
      </div>
    );
  }

  const percentage = limit > 0 ? (currentUsage / limit) * 100 : 0;

  return (
    <div className="text-sm text-zinc-500">
      <span className={`font-medium ${percentage >= 80 ? "text-amber-600" : "text-zinc-900"}`}>
        {currentUsage}
      </span>
      {" / "}
      <span>{limit}</span> {label}
    </div>
  );
}

/**
 * Gated content wrapper - shows nothing if not entitled
 * Also hides if feature is globally inactive
 */
export function GatedContent({ featureSlug, children, fallback = null }) {
  const { isEntitled, isFeatureActive, loading } = useEntitlements();

  if (loading) return null;
  
  // If feature is globally inactive, show fallback (or nothing)
  if (!isFeatureActive(featureSlug)) return fallback;
  
  // If not entitled, show fallback
  if (!isEntitled(featureSlug)) return fallback;
  
  return children;
}

/**
 * Shows content only if feature is globally active (regardless of entitlement)
 * Use for feature-specific UI that should be hidden when admin deactivates feature
 */
export function FeatureActiveContent({ featureSlug, children, fallback = null }) {
  const { isFeatureActive, loading } = useEntitlements();

  if (loading) return null;
  if (!isFeatureActive(featureSlug)) return fallback;
  return children;
}

export default {
  FeatureCTAButton,
  FeatureCTACard,
  LockedFeatureOverlay,
  UsageLimitWarning,
  UsageLimitDisplay,
  GatedContent,
  FeatureActiveContent,
  FEATURE_CONFIG,
};
